import type {
  ExamLevel,
  ExamType,
  ModuleResult,
  SimulationResult,
} from "@/types/simulation-results";
import type { ModuleSubmission, UserSubmission } from "./types";

export const PREVIEW_MODULE_KEYS: readonly ModuleResult["module"][] = [
  "reading",
  "listening",
  "writing",
  "speaking",
];

export const isPreviewModuleKey = (value: string): value is ModuleResult["module"] =>
  PREVIEW_MODULE_KEYS.includes(value as ModuleResult["module"]);

export const getSubmissionModuleAnswerCount = (module: ModuleSubmission): number =>
  module.answer_count ?? module.answers?.length ?? 0;

const parseModuleScore = (score: string | number | null | undefined): number => {
  if (score === null || score === undefined) {
    return 0;
  }

  const numeric = typeof score === "string" ? Number.parseFloat(score) : Number(score);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const buildPreviewSessionModules = (
  submission: UserSubmission | null,
): ModuleResult[] => {
  if (!submission) return [];

  return submission.modules
    .filter((module) => isPreviewModuleKey(module.module))
    .map((module) => {
      const score = parseModuleScore(module.score);
      const hasAnswers = getSubmissionModuleAnswerCount(module) > 0;
      return {
        module: module.module as ModuleResult["module"],
        score,
        maxScore: 100,
        percentage: score,
        passed: score >= 60,
        is_graded: hasAnswers,
        analysis_status: hasAnswers ? "succeeded" : null,
        analysis_error_code: null,
        analysis_attempts: null,
        analysis_can_retry: false,
        analysis_updated_at: null,
        section_feedback_status: null,
      };
    });
};

export const buildSubmissionSimulationResult = (
  submission: UserSubmission,
): SimulationResult => {
  const modules = buildPreviewSessionModules(submission);
  const totalScore = modules.reduce((sum, module) => sum + module.score, 0);
  const maxTotalScore = modules.length * 100;
  const percentage = maxTotalScore > 0
    ? Math.round((totalScore / maxTotalScore) * 100 * 10) / 10
    : 0;

  return {
    id: submission.session_id,
    date: submission.created_at,
    examType: submission.exam_type as ExamType,
    level: (submission.level?.[0] ?? "A1") as ExamLevel,
    totalScore: Math.round(totalScore * 10) / 10,
    maxTotalScore,
    percentage,
    passed: modules.length > 0 && modules.every((module) => module.passed),
    duration: 0,
    modules,
  };
};
